import combinedChartTemplate from './combinedChart.html';
import './combinedChart.scss';

export default angular
  .module('main.dashboardPanel.combinedChart', [])

  .directive('combinedChart', () => {
    return {
      restrict: 'E',
      template: combinedChartTemplate,
      scope: {
        series: '=', // bar data series array {data: {x: number, y: number}[], label: string}[]
        min: '=', // minimum axis value
        max: '=', // maximum axis value
        stacked: '=' // stacked chart
      },
      controller: ($scope, $filter, $element) => {
        const $t = $filter('translate');

        const colorHover = 'rgba(142, 167, 218, 1)';
        const colorRedish = 'rgba(234, 110, 104, 1)';
        const colorBlack = '#555555';

        const datasetColors = {
          gray: 'rgba(0, 0, 0, 0.1)',
          grey: 'rgba(0, 0, 0, 0.1)',
          blue: 'rgba(132, 166, 238, 0.9)',
          green: 'rgba(92, 210, 62, 0.5)',
          yellow: 'rgba(230, 181, 69, 0.4)',
          red: 'rgba(200, 30, 30, 0.4)',
          purple: 'rgba(183, 39, 230, 0.4)',
          cyan: 'rgba(39,168,230,0.4)'
        };

        const lineChartOptions = {
          // Line styles
          fill: false, // do not fill area
          borderColor: colorRedish,
          borderWidth: 2,

          // Points
          pointRadius: 5,
          pointBorderWidth: 2,
          pointBorderColor: colorRedish,
          pointBackgroundColor: '#ffffff',
          pointHoverRadius: 5,
          pointHoverBorderColor: colorBlack,
          pointHoverBackgroundColor: '#ffffff',
          pointHoverBorderWidth: 2,
        };

        let chart;

        init();

        function init() {
          $scope.$watch('series', () => {
            if ($scope.series) {
              drawChart();
            }
          }, true);

          $scope.$on('$destroy', function() {
            if (chart) {
              chart.destroy();
            }
          });
        }

        function drawChart() {
          if (!chart) {
            createChart();
          }

          $scope.showNoDataMessage = false;
          chart.data.datasets = chart.data.datasets || [];

          // Clear old data
          chart.data.datasets.forEach(ds => {
            ds.data = [];
          });

          // Apply input data into datasets
          composeDatasets($scope.series);

          $scope.showNoDataMessage = !$scope.series || !$scope.series.length || $scope.series.every(s => !s.data || !s.data.length);

          let timeValues = $scope.series?.map(series => series.data.map(d => d.x)).flat();

          // Set min and max OX axis values
          chart.options.scales.xAxes[0].ticks.min = $scope.min || (timeValues?.length
            ? moment(Math.min(...timeValues)).startOf('d').valueOf()
            : undefined);
          chart.options.scales.xAxes[0].ticks.max = ($scope.max ? $scope.max - 1 : undefined) || (timeValues?.length
            ? moment(Math.max(...timeValues)).endOf('d').valueOf()
            : undefined);

          chart.options.scales.xAxes[0].stacked = !!$scope.stacked;
          chart.options.scales.yAxes[0].stacked = !!$scope.stacked;

          chart.update();
        }

        /**
         * Compose dataset(s) out of combined data of specified types
         * @param {Array<{}>} dataset Series of data
         */
        function composeDatasets(data) {
          data?.forEach((series, index) => {
            let dataset = chart.data.datasets[index];
            if (!dataset) {
              // Create dataset if it was not created yet
              dataset = {};
              chart.data.datasets.push(dataset);
            }

            dataset.label = series.label;

            if (series.color && datasetColors[series.color] && !series.type) {
              dataset.backgroundColor = datasetColors[series.color];
            } else {
              let color = Object.keys(datasetColors)
                .find(c => !chart.data.datasets.some(ds => ds.backgroundColor === datasetColors[c]));
              dataset.backgroundColor = datasetColors[color];
            }
            dataset.order = index;

            // For line type of serie
            if (series.type) {
              dataset.type = series.type;
              dataset.order = 0; // put on top of other series
              angular.merge(dataset, lineChartOptions);
            }

            dataset.data = angular.copy(series.data || [])
              .filter((datum, index, arr) => { // Filter out multiple values for the same day
                return !arr.some((d, i) => i > index && moment(datum.x).isSame(moment(d.x), 'day'));
              });
          });
        }

        function createChart() {
          let ctx = $($element).find('#combinedChart')[0];
          Chart.defaults.global.defaultFontFamily = '\'Roboto\', Arial, sans-serif';

          Chart.Tooltip.positioners.custom = function(elements, eventPosition) {
            if (!elements.length) {
              return false;
            }
            let x = elements.reduce((acc, e) => e._model.x + acc, 0) / elements.length;
            let height = Math.max(...elements.map(e => e._model.base - e._model.y));
            let y = elements.find(e => height === e._model.base - e._model.y)._model.y;
            return { x, y: y + (height / 2) };
          };

          chart = new Chart(ctx, {
            type: 'bar',
            data: {
              datasets: [
                {
                  label: 'Data',
                  data: []
                }
              ]
            },
            options: {
              layout: {
                padding: {
                  top: 10
                }
              },
              scales: {
                xAxes: [
                  {
                    type: 'time',
                    stacked: false,
                    time: {
                      timezone: moment.tz.guess(),
                      unit: 'day',
                      parser: function(date) {
                        return moment(date).tz(moment.tz.guess()).startOf('d'); // fixing offsetGridLines bug
                      }
                    },
                    offset: true,
                    ticks: {
                      maxRotation: 0,
                      autoSkipPadding: 12
                    },
                    gridLines: {
                      borderDash: [4, 4],
                      zeroLineBorderDash: [4, 4],
                      drawTicks: true,
                      offsetGridLines: true,
                      color: 'rgba(0, 0, 0, 0.15)',
                      zeroLineColor: 'rgba(0, 0, 0, 0.15)'
                    }
                  }
                ],
                yAxes: [
                  {
                    ticks: {
                      maxTicksLimit: 6,
                      suggestedMin: 0,
                      callback: value => value % 1 === 0 ? value : undefined // skip non-whole values
                    },
                    gridLines: {
                      drawTicks: true,
                      drawBorder: true,
                      drawOnChartArea: false,
                      zeroLineColor: 'rgba(0, 0, 0, 0)',
                      borderDash: [0, 20],
                    }
                  }
                ]
              },
              legend: {
                display: false
              },
              hover: {
                mode: 'nearest',
                intersect: false
              },
              tooltips: {
                animationDuration: 0,
                caretPadding: 12,
                cornerRadius: 3,
                displayColors: true,
                backgroundColor: '#ffffff',
                borderColor: 'rgba(0,0,0,0.05)',
                borderWidth: 1,
                titleFontColor: colorBlack,
                bodyFontColor: colorBlack,
                xPadding: 12,
                yPadding: 8,
                xAlign: 'center',
                yAlign: 'bottom',

                // Hovering preference
                mode: 'index',
                axis: 'x',
                intersect: false,
                position: $scope.stacked ? 'average' : 'custom',

                callbacks: {
                  title: function(tooltipItems, data) {
                    let dataObj = data.datasets[tooltipItems[0].datasetIndex].data[tooltipItems[0].index];
                    let date = dataObj.x;
                    return moment(date).format('ll');
                  },
                  labelColor: function(tooltipItem, data) {
                    let dataObj = data.config.data.datasets[tooltipItem.datasetIndex];
                    return {
                      backgroundColor: dataObj.borderColor || dataObj.backgroundColor || '#dddddd',
                      borderColor: '#ffffff'
                    };
                  }
                }
              },
              animation: {
                duration: 300
              }
            }
          });
        }
      }
    };
  })
  .name;
